import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { config } from '../config';
import { authService } from '../services/auth';

interface QueueItem {
  segmentId: string;
  partId: string;
  queuePosition?: number;
  estimatedWaitTime?: number;
  status: 'queued' | 'processing' | 'completed' | 'error';
  error?: string;
  audioFile?: string;
  duration?: number;
}

interface SegmentData {
  segmentId: string;
  dbSegmentId?: string;
  scriptId?: string;
  partId: string;
  text: string;
  speed?: number;
  inputAudioFile?: string;
}

interface QueueCallbacks {
  onSegmentCompleted?: (segmentId: string, partId: string, audioFile: string, duration?: number, ttsCacheId?: string) => void;
  onSegmentError?: (segmentId: string, partId: string, error: string) => void;
}

export const useTTSQueue = (callbacks?: QueueCallbacks) => {
  const [queueItems, setQueueItems] = useState<Map<string, QueueItem>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const [globalQueueLength, setGlobalQueueLength] = useState(0);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callbacksRef = useRef(callbacks);
  const isMountedRef = useRef(true);

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  // Connect to SSE endpoint - stable reference with no dependencies on callbacks
  const connectSSE = useCallback(() => {
    const token = authService.getToken();
    if (!token) {
      console.error('No auth token available for SSE connection');
      return;
    }

    // Don't create a new connection if one already exists and is connecting/open
    if (eventSourceRef.current && 
        (eventSourceRef.current.readyState === EventSource.CONNECTING || 
         eventSourceRef.current.readyState === EventSource.OPEN)) {
      console.log('SSE connection already exists, skipping reconnection');
      return;
    }

    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // EventSource doesn't support custom headers, so we pass token as query parameter
    const eventSource = new EventSource(
      `${config.apiUrl}/tts-queue-events?token=${encodeURIComponent(token)}`
    );

    eventSource.onopen = () => {
      console.log('SSE connection established');
      setIsConnected(true);
      
      // Clear any reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'connected':
            console.log('SSE connected with client ID:', data.clientId);
            break;
            
          case 'queueStatus':
          case 'queueUpdate':
            setGlobalQueueLength(data.queueLength);
            
            // Update queue items with new positions
            if (data.userItems) {
              setQueueItems(prev => {
                const updated = new Map(prev);
                
                data.userItems.forEach((item: any) => {
                  const existing = updated.get(item.segmentId);
                  if (existing) {
                    updated.set(item.segmentId, {
                      ...existing,
                      queuePosition: item.queuePosition,
                      estimatedWaitTime: item.estimatedWaitTime
                    });
                  }
                });
                
                return updated;
              });
            }
            break;
            
          case 'segmentUpdate':
            setQueueItems(prev => {
              const updated = new Map(prev);
              const existing = updated.get(data.segmentId);
              
              if (existing) {
                const updatedItem: QueueItem = {
                  ...existing,
                  status: data.status
                };
                
                if (data.status === 'completed') {
                  updatedItem.audioFile = data.audioFile;
                  updatedItem.duration = data.duration;
                  updatedItem.queuePosition = undefined;
                  updatedItem.estimatedWaitTime = undefined;
                  
                  // Call completion callback only if component is still mounted
                  // Use setTimeout to defer callback execution until after render cycle
                  if (isMountedRef.current && callbacksRef.current?.onSegmentCompleted) {
                    setTimeout(() => {
                      if (isMountedRef.current && callbacksRef.current?.onSegmentCompleted) {
                        callbacksRef.current.onSegmentCompleted(data.segmentId, existing.partId, data.audioFile, data.duration, data.ttsCacheId);
                      }
                    }, 0);
                  }
                } else if (data.status === 'error') {
                  updatedItem.error = data.error;
                  updatedItem.queuePosition = undefined;
                  updatedItem.estimatedWaitTime = undefined;
                  
                  // Call error callback only if component is still mounted
                  // Use setTimeout to defer callback execution until after render cycle
                  if (isMountedRef.current && callbacksRef.current?.onSegmentError) {
                    setTimeout(() => {
                      if (isMountedRef.current && callbacksRef.current?.onSegmentError) {
                        callbacksRef.current.onSegmentError(data.segmentId, existing.partId, data.error);
                      }
                    }, 0);
                  }
                }
                
                updated.set(data.segmentId, updatedItem);
              }
              
              return updated;
            });
            break;
            
          case 'heartbeat':
            // Ignore heartbeat messages
            break;
            
          default:
            console.log('Unknown SSE message type:', data.type);
        }
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      setIsConnected(false);
      
      // Only attempt reconnection if the component is still mounted
      // and we don't already have a reconnect timeout
      if (isMountedRef.current && !reconnectTimeoutRef.current) {
        console.log('Scheduling SSE reconnection in 5 seconds...');
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          if (isMountedRef.current) {
            console.log('Attempting to reconnect SSE...');
            connectSSE();
          }
        }, 5000);
      }
    };

    eventSourceRef.current = eventSource;
  }, []);

  // Connect on mount
  useEffect(() => {
    isMountedRef.current = true; // Ensure mounted flag is set on mount
    connectSSE();
    
    return () => {
      isMountedRef.current = false;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, []); // Remove connectSSE dependency to prevent re-runs

  // Queue a single segment
  const queueSegment = useCallback(async (
    segment: SegmentData,
    voiceId: string
  ) => {
    try {
      // Add to local queue immediately
      setQueueItems(prev => {
        const updated = new Map(prev);
        updated.set(segment.segmentId, {
          segmentId: segment.segmentId,
          partId: segment.partId,
          status: 'queued'
        });
        return updated;
      });

      const token = authService.getToken();
      const response = await fetch(`${config.apiUrl}/generate-segment-speech`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          segmentId: segment.segmentId,
          dbSegmentId: segment.dbSegmentId,
          scriptId: segment.scriptId,
          partId: segment.partId,
          text: segment.text,
          voiceId: voiceId,
          speed: segment.speed,
          audioFileUrl: segment.inputAudioFile
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      
      // Update queue item with initial position
      setQueueItems(prev => {
        const updated = new Map(prev);
        const existing = updated.get(segment.segmentId);
        if (existing) {
          updated.set(segment.segmentId, {
            ...existing,
            queuePosition: result.queuePosition,
            estimatedWaitTime: result.estimatedWaitTime
          });
        }
        return updated;
      });

      return result;
    } catch (error) {
      // Update queue item with error
      setQueueItems(prev => {
        const updated = new Map(prev);
        const existing = updated.get(segment.segmentId);
        if (existing) {
          updated.set(segment.segmentId, {
            ...existing,
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
        return updated;
      });
      
      throw error;
    }
  }, []);

  // Queue multiple segments at once
  const queueBatch = useCallback(async (
    segments: SegmentData[],
    voiceId: string
  ) => {
    try {
      // Add all to local queue immediately
      setQueueItems(prev => {
        const updated = new Map(prev);
        segments.forEach(segment => {
          updated.set(segment.segmentId, {
            segmentId: segment.segmentId,
            partId: segment.partId,
            status: 'queued'
          });
        });
        return updated;
      });

      const token = authService.getToken();
      const response = await fetch(`${config.apiUrl}/generate-batch-speech`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          segments: segments.map(seg => ({
            segmentId: seg.segmentId,
            dbSegmentId: seg.dbSegmentId,
            scriptId: seg.scriptId,
            partId: seg.partId,
            text: seg.text,
            speed: seg.speed,
            inputAudioFile: seg.inputAudioFile
          })),
          voiceId: voiceId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      
      // Update queue items with positions
      setQueueItems(prev => {
        const updated = new Map(prev);
        result.segments.forEach((segResult: any) => {
          const existing = updated.get(segResult.segmentId);
          if (existing) {
            updated.set(segResult.segmentId, {
              ...existing,
              queuePosition: segResult.queuePosition,
              estimatedWaitTime: segResult.estimatedWaitTime
            });
          }
        });
        return updated;
      });

      return result;
    } catch (error) {
      // Update all segments with error
      setQueueItems(prev => {
        const updated = new Map(prev);
        segments.forEach(segment => {
          const existing = updated.get(segment.segmentId);
          if (existing) {
            updated.set(segment.segmentId, {
              ...existing,
              status: 'error',
              error: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        });
        return updated;
      });
      
      throw error;
    }
  }, []);

  // Clear completed items from queue
  const clearCompleted = useCallback(() => {
    setQueueItems(prev => {
      const updated = new Map(prev);
      Array.from(updated.entries()).forEach(([key, item]) => {
        if (item.status === 'completed' || item.status === 'error') {
          updated.delete(key);
        }
      });
      return updated;
    });
  }, []);

  // Get queue status for a specific segment
  const getSegmentStatus = useCallback((segmentId: string): QueueItem | undefined => {
    return queueItems.get(segmentId);
  }, [queueItems]);

  // Get all queue items for a specific part
  const getPartQueueItems = useCallback((partId: string): QueueItem[] => {
    return Array.from(queueItems.values()).filter(item => item.partId === partId);
  }, [queueItems]);

  return {
    queueSegment,
    queueBatch,
    clearCompleted,
    getSegmentStatus,
    getPartQueueItems,
    queueItems: Array.from(queueItems.values()),
    globalQueueLength,
    isConnected
  };
};